import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
        document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = newLang;
    };

    const categories = [
        { key: 'quran', label: t('categories.quran', 'Quran') },
        { key: 'hadith', label: t('categories.hadith', 'Hadith') },
        { key: 'prophet', label: t('categories.prophet', 'Prophet') },
        { key: 'sahabas', label: t('categories.sahabas', 'Sahabas') },
        { key: 'scholars', label: t('categories.scholars', 'Scholars') },
        { key: 'sects', label: t('categories.sects', 'Sects') },
        { key: 'legalSchools', label: t('categories.legalSchools', 'Legal Schools') },
        { key: 'timeline', label: t('categories.timeline', 'Timeline') }
    ];

    return (
        <nav className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-14">
                    {/* Logo and Badge */}
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center">
                            <Logo />
                            <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-yellow-500 rounded-full animate-pulse">
                                Under Development
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex md:items-center md:space-x-8">
                        {categories.map((category) => (
                            <Link
                                key={category.key}
                                to={`/category/${category.key}`}
                                className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors duration-200"
                            >
                                {category.label}
                            </Link>
                        ))}
                        <div className="h-4 w-px bg-gray-200"></div>
                        <button
                            onClick={toggleLanguage}
                            className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors duration-200"
                        >
                            {i18n.language === 'en' ? 'العربية' : 'English'}
                        </button>
                        <Link
                            to="/profile"
                            className="text-gray-700 hover:text-green-600 transition-colors duration-200"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </Link>
                    </div>

                    {/* Mobile menu button */}
                    <div className="flex items-center md:hidden">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-gray-700 hover:text-green-600 focus:outline-none transition-colors duration-200"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {isMenuOpen && (
                <div className="md:hidden fixed inset-0 z-50 bg-white">
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                            <div className="flex items-center">
                                <Logo />
                                <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-yellow-500 rounded-full animate-pulse">
                                    Under Development
                                </span>
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className="text-gray-700 hover:text-green-600 focus:outline-none"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Mobile menu items */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="px-4 py-4 space-y-4">
                                {categories.map((category) => (
                                    <Link
                                        key={category.key}
                                        to={`/category/${category.key}`}
                                        className="block text-base font-medium text-gray-700 hover:text-green-600 transition-colors duration-200"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        {category.label}
                                    </Link>
                                ))}
                                <div className="h-px bg-gray-200"></div>
                                <button
                                    onClick={toggleLanguage}
                                    className="block w-full text-left text-base font-medium text-gray-700 hover:text-green-600 transition-colors duration-200"
                                >
                                    {i18n.language === 'en' ? 'العربية' : 'English'}
                                </button>
                                <Link
                                    to="/profile"
                                    className="block text-base font-medium text-gray-700 hover:text-green-600 transition-colors duration-200"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {t('nav.profile', 'Profile')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar; 